import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, PORTAL_TOKEN_KEY, TOKEN_KEY } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

export default function PaymentReturn({ mode = "success" }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);
  const backTo = localStorage.getItem(PORTAL_TOKEN_KEY) ? "/portal" : "/app/billing";

  useEffect(() => {
    if (mode !== "success" || !sessionId) return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const authToken = localStorage.getItem(PORTAL_TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (_) {}
      if (attempts >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [mode, sessionId]);

  const render = () => {
    if (state === "checking") return { icon: <Loader2 className="h-10 w-10 animate-spin text-primary" />, title: "Confirming your payment…", desc: "Please wait while we verify the transaction with the gateway." };
    if (state === "paid") return { icon: <CheckCircle2 className="h-10 w-10 text-success" />, title: "Payment successful 🎉", desc: `${info?.invoice_number ? info.invoice_number + " · " : ""}₹${Number(info?.amount || 0).toLocaleString("en-IN")} received. A receipt has been generated.` };
    if (state === "cancelled") return { icon: <XCircle className="h-10 w-10 text-muted-foreground" />, title: "Payment cancelled", desc: "No charge was made. You can retry the payment anytime." };
    if (state === "failed") return { icon: <XCircle className="h-10 w-10 text-critical" />, title: "Payment not completed", desc: "The transaction did not go through. Please try again." };
    return { icon: <Clock className="h-10 w-10 text-warning" />, title: "Still processing", desc: "Your payment is taking a moment to confirm. It will update automatically once verified." };
  };
  const r = render();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute inset-0 bc-grid-bg opacity-30" />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-xl" data-testid="payment-return">
        <div className="mb-6 flex justify-center"><Link to="/"><Logo /></Link></div>
        <div className="mb-4 flex justify-center">{r.icon}</div>
        <h1 className="font-heading text-xl font-bold">{r.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{r.desc}</p>
        <Link to={backTo}><Button className="mt-6 w-full rounded-full" data-testid="payment-return-back">Back to {backTo === "/portal" ? "portal" : "billing"}</Button></Link>
      </div>
    </div>
  );
}
